import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Heart, Scale, Briefcase, CheckCircle2, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, fadeInUp, staggerContainer, springPresets } from '@/lib/index';
import { practiceAreas, services } from '@/data/index';
import { ServiceCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';
const IconMap: Record<string, React.ReactNode> = {
  Brain: <Brain className="w-8 h-8" />,
  Heart: <Heart className="w-8 h-8" />,
  Scale: <Scale className="w-8 h-8" />,
  Briefcase: <Briefcase className="w-8 h-8" />
};
export default function Home() {
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-background">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,_var(--color-primary)_0%,_transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl">
            <motion.div variants={fadeInUp}>
              <span className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                Psycholog • Seksuolog • Mediator
              </span>
            <h1 className="text-5xl lg:text-7xl font-serif text-foreground leading-tight mb-6">
              Z pasją, sercem i <span className="text-accent italic">doświadczeniem</span>
            </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Nazywam się Rachela Nowak. Pomagam przechodzić przez kryzysy, budować odporność psychiczną, naprawiać komunikację i podejmować ważne życiowe decyzje — prywatnie i zawodowo.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="rounded-full px-8">
                  <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                    Umów wizytę <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                  <Link to={ROUTE_PATHS.SERVICES}>Poznaj ofertę</Link>
                </Button>
              </div>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          ...springPresets.smooth,
          delay: 0.2
        }} className="relative aspect-[4/5] lg:aspect-square max-w-lg mx-auto lg:ml-auto">
            <div className="absolute -inset-4 border border-border rounded-2xl z-0 transform rotate-3" />
            <div className="absolute -inset-4 bg-accent/5 rounded-2xl z-0 transform -rotate-2" />
            <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2028082643843993600_zdjecie_512x512_1.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Rachela Nowak" className="relative z-10 w-full rounded-2xl shadow-2xl object-center h-[512px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Core Areas Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-serif mb-4">Obszary mojej pracy</h2>
            <p className="text-muted-foreground">
              Łączę wiedzę z kilku światów — psychologii, seksuologii, prawa i biznesu — by wspierać ludzi w sposób możliwie pełny i realny.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {practiceAreas.map((area, index) => <motion.div key={area.title} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} transition={{
            delay: index * 0.1
          }} className="bg-card p-8 rounded-2xl border border-border hover:shadow-lg transition-all">
                <div className="text-primary mb-6">
                  {IconMap[area.iconName] || <Brain className="w-8 h-8" />}
                </div>
                <h3 className="text-xl font-semibold mb-3">{area.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {area.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2026731645145890816_trenerka_prezentacja_compressed_1.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Gabinet terapeutyczny" className="rounded-3xl shadow-xl aspect-video lg:aspect-square h-[400px] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl lg:text-4xl font-serif mb-8">Dlaczego warto podjąć współpracę?</h2>
              <div className="space-y-6">
                {["Konkret i skuteczność zamiast pustych haseł", "Interdyscyplinarne podejście (MBA, Prawo, Psychologia)", "Doświadczenie w pracy z osobami indywidualnymi i biznesem", "Bezpieczna przestrzeń oparta na empatii i poufności"].map((item, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.1
              }} className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-accent shrink-0 mt-1" />
                    <p className="text-lg text-foreground/80">{item}</p>
                  </motion.div>)}
              </div>
              <div className="mt-12 p-8 bg-primary/5 rounded-2xl border-l-4 border-primary italic text-lg">
                <Quote className="w-8 h-8 text-primary/20 mb-4" />
                <p className="mb-4">
                  "Działam tam, gdzie potrzeba odwagi, rozmowy i zmiany systemu. Nie sprzedaję pustych haseł — skupiam się na tym, co działa."
                </p>
                <span className="font-semibold not-italic text-primary">— Rachela Nowak</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl lg:text-4xl font-serif mb-4">Jak mogę Ci pomóc?</h2>
              <p className="text-muted-foreground">
                Moje usługi są dostosowane do indywidualnych potrzeb, niezależnie od tego, czy potrzebujesz wsparcia w gabinecie, czy w organizacji.
              </p>
            </div>
            <Button asChild variant="ghost" className="text-primary hover:text-primary hover:bg-primary/5">
              <Link to={ROUTE_PATHS.SERVICES} className="flex items-center">
                Zobacz wszystkie usługi <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map(service => <ServiceCard key={service.id} service={service} />)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={IMAGES.WELLNESS_PEACE_1} alt="Tło sekcji kontaktowej" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="relative z-10 bg-primary/95 text-primary-foreground p-12 lg:p-24 text-center">
              <h2 className="text-4xl lg:text-5xl font-serif mb-6">
                Zrób pierwszy krok w stronę zmiany
              </h2>
              <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
                Zapraszam na konsultację wstępną, podczas której omówimy Twoje potrzeby i ustalimy plan dalszego działania.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-10">
                  <Link to={ROUTE_PATHS.CONTACT}>Zarezerwuj termin</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white/10 rounded-full px-10">
                  <Link to={ROUTE_PATHS.ABOUT}>Dowiedz się więcej o mnie</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}